import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid2";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { Select, MenuItem, Checkbox, InputLabel, Alert } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";

// API
import ProcesosApi from "api/procesosApi";

// Styles
import useStyles from "./styles";

function ClienteForm({ formData, setFormData, setError, procesoCreado = false, procesoId = null }) {
  const classes = useStyles();
  const [clienteExistente, setClienteExistente] = useState(!!formData.clienteIdentificacion);

  // Mapeo de campos para el backend
  const FIELD_MAPPING = {
    poseeBienes: "posee_bienes",
    procesosJudiciales: "procesos_judiciales",
    personasACargo: "personas_a_cargo",
    sociedadesProceso: "sociedades",
  };

  const handleChange = (field) => (event) => {
    if (
      procesoCreado &&
      ![
        "compromisoMensual",
        "poseeBienes",
        "procesosJudiciales",
        "personasACargo",
        "sociedadesProceso",
        "causas",
      ].includes(field)
    ) {
      return;
    }

    setFormData((prevData) => {
      const newData = {
        ...prevData,
        [field]: event.target.value,
      };

      return newData;
    });
    setError(undefined);
  };

  const handleCheckboxChange = (field) => async (event) => {
    const newValue = event.target.checked;
    if (procesoCreado) {
      try {
        if (!procesoId) {
          throw new Error("No hay ID de proceso disponible");
        }
        // Si estamos quitando el check, confirmar y eliminar datos relacionados
        if (!newValue) {
          const willDelete = window.confirm(
            "¿Está seguro(a)? Se eliminarán todos los datos relacionados."
          );
          if (!willDelete) {
            // Si el usuario cancela, revertimos el UI
            setFormData((prev) => ({
              ...prev,
              [field]: !newValue,
            }));
            return;
          }
          try {
            let response;
            switch (field) {
              case "poseeBienes": {
                response = await ProcesosApi.obtenerBienesProceso(procesoId);
                if (response.data.bienes?.length > 0) {
                  await Promise.all(
                    response.data.bienes.map((bien) => ProcesosApi.eliminarBien(procesoId, bien.id))
                  );
                }
                break;
              }
              case "procesosJudiciales": {
                response = await ProcesosApi.obtenerProcesosJudiciales(procesoId);
                if (response.data?.length > 0) {
                  await Promise.all(
                    response.data.map((proceso) =>
                      ProcesosApi.eliminarProcesoJudicial(procesoId, proceso.id)
                    )
                  );
                }
                break;
              }
              case "personasACargo": {
                response = await ProcesosApi.obtenerPersonasCargo(procesoId);
                if (response.data?.length > 0) {
                  await Promise.all(
                    response.data.map((persona) =>
                      ProcesosApi.eliminarPersonaCargo(procesoId, persona.id)
                    )
                  );
                }
                break;
              }
              case "sociedadesProceso": {
                response = await ProcesosApi.obtenerSociedades(procesoId);
                if (response.data?.length > 0) {
                  await Promise.all(
                    response.data.map((sociedad) =>
                      ProcesosApi.eliminarSociedad(procesoId, sociedad.id)
                    )
                  );
                }
                break;
              }
              default:
                break;
            }
          } catch (error) {
            throw new Error("Error al eliminar los datos relacionados");
          }
        }
        // Preparar la actualización
        const snakeCaseField = FIELD_MAPPING[field];
        const updateData = {
          [snakeCaseField]: newValue,
        };
        // Actualizar en el backend
        const response = await ProcesosApi.actualizarProceso(procesoId, updateData);
        if (!response.data) {
          throw new Error("La respuesta no contiene datos");
        }
        if (response.status !== 200) {
          throw new Error(`La actualización falló con estado ${response.status}`);
        }
        // Verificar que el valor se actualizó correctamente
        const updatedValue = response.data[snakeCaseField];
        if (updatedValue !== newValue) {
          throw new Error("El servidor no actualizó el valor correctamente");
        }
        // Actualizar el estado local solo si todo fue exitoso
        setFormData((prev) => ({
          ...prev,
          [field]: newValue,
          [snakeCaseField]: newValue, // También actualizamos la versión snake_case
        }));
        // Limpiar cualquier error previo
        setError(undefined);
      } catch (error) {
        // Revertir el UI
        setFormData((prev) => ({
          ...prev,
          [field]: !newValue,
          [FIELD_MAPPING[field]]: !newValue,
        }));
        // Mostrar error
        setError(error.message || "Error al actualizar el estado del proceso");
      }
    } else {
      // Si no está creada, actualizar ambas versiones del campo en el estado local
      setFormData((prev) => ({
        ...prev,
        [field]: newValue,
        [FIELD_MAPPING[field]]: newValue,
      }));
      setError(undefined);
    }
  };

  const handleDateChange = (newValue) => {
    if (procesoCreado) return;

    if (newValue) {
      const year = newValue.$y;
      const month = String(newValue.$M + 1).padStart(2, "0");
      const day = String(newValue.$D).padStart(2, "0");
      const fecha = `${year}-${month}-${day}`;
      setFormData({
        ...formData,
        clienteFechaNacimiento: fecha,
      });
    } else {
      setFormData({
        ...formData,
        clienteFechaNacimiento: null,
      });
    }
  };

  const handleIdentificacionChange = async (event) => {
    if (!event || !event.target) return;
    if (procesoCreado) return;

    setError(null);
    const identificacion = event.target.value;
    // Se valida que la Identificacion sea NUMERICO y que sea de 1 a 15 digitos
    if (!/^\d{0,20}$/.test(identificacion)) {
      setError("La Identificación del Cliente debe tener sólo números, de 5 a 20 dígitos.");
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      clienteIdentificacion: identificacion,
    }));

    if (!identificacion || identificacion.length < 8) {
      setClienteExistente(false);
      setFormData((prevData) => ({
        ...prevData,
        clienteIdentificacion: identificacion,
        clienteTipoIdentificacion: "",
        clienteNombres: "",
        clienteApellidos: "",
        clienteEmail: "",
        clienteCelular: "",
        clienteDireccion: "",
        clienteGenero: "",
        clienteTipoTrabajador: "",
        clienteTipoPersona: "",
        clienteSector: "",
        clienteFechaNacimiento: null,
        nombre: "",
        resp_asistente: null,
      }));
      return;
    }

    try {
      const clientesResponse = await ProcesosApi.fetchClienteByIdentificacion(identificacion);
      const cliente = clientesResponse.data;
      if (cliente && !cliente.error) {
        setClienteExistente(true);
        setFormData((prevData) => {
          const newData = {
            ...prevData,
            clienteId: cliente.id || "",
            clienteTipoIdentificacion: cliente.tipo_identificacion || "",
            clienteNombres: cliente.nombres || "",
            clienteApellidos: cliente.apellidos || "",
            clienteEmail: cliente.email || "",
            clienteCelular: cliente.celular || "",
            clienteDireccion: cliente.direccion || "",
            clienteGenero: cliente.genero || "",
            clienteTipoTrabajador: cliente.tipo_trabajador || "",
            clienteTipoPersona: cliente.tipo_persona || "",
            clienteSector: cliente.sector_trabajador || "",
            clienteFechaNacimiento: cliente.fecha_nacimiento || null,
            resp_asistente: null,
          };
          return newData;
        });
      } else {
        setClienteExistente(false);
        setFormData((prevData) => ({
          ...prevData,
          clienteId: "",
          clienteTipoIdentificacion: "",
          clienteNombres: "",
          clienteApellidos: "",
          clienteEmail: "",
          clienteCelular: "",
          clienteDireccion: "",
          clienteGenero: "",
          clienteTipoTrabajador: "",
          clienteTipoPersona: "",
          clienteSector: "",
          clienteFechaNacimiento: null,
          nombre: "",
          resp_asistente: null,
        }));
      }
      //setError(null);
    } catch (error) {
      setError(obtenerMensajeError(error, "Error al verificar los datos del cliente"));
      setClienteExistente(false);
    }
  };

  return (
    <SuiBox>
      {(clienteExistente || procesoCreado) && (
        <SuiBox mb={2}>
          <Alert severity="info" className={classes.alert}>
            {procesoCreado
              ? "Proceso existente - Solo se pueden modificar los checks y causas"
              : clienteExistente
              ? "Deudor existente - Los campos personales no pueden ser modificados"
              : null}
          </Alert>
        </SuiBox>
      )}

      <SuiBox className={classes.formSection}>
        <SuiTypography variant="h6">Datos del Deudor</SuiTypography>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Identificación
            </SuiTypography>
            <SuiInput
              value={formData.clienteIdentificacion}
              onChange={handleIdentificacionChange}
              onBlur={handleIdentificacionChange}
              placeholder="Numero Identificación. [0-9], de 5 a 20 dígitos."
              required
              disabled={procesoCreado}
              readOnly={procesoCreado}
            />
          </Grid>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Tipo Identificación
            </SuiTypography>
            <Select
              value={formData.clienteTipoIdentificacion}
              onChange={handleChange("clienteTipoIdentificacion")}
              displayEmpty
              fullWidth
              disabled={clienteExistente || procesoCreado}
              className={classes.select}
              sx={clienteExistente || procesoCreado ? { opacity: 0.8 } : {}}
            >
              <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
              <MenuItem value="NIT">Número de Identificación Tributaria</MenuItem>
              <MenuItem value="CE">Cédula de Extranjería</MenuItem>
              <MenuItem value="PAS">Pasaporte</MenuItem>
              <MenuItem value="OTRO">Otro</MenuItem>
            </Select>
          </Grid>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Nombres
            </SuiTypography>
            <SuiInput
              value={formData.clienteNombres}
              onChange={handleChange("clienteNombres")}
              required
              disabled={clienteExistente || procesoCreado}
              readOnly={clienteExistente || procesoCreado}
            />
          </Grid>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Apellidos
            </SuiTypography>
            <SuiInput
              value={formData.clienteApellidos}
              onChange={handleChange("clienteApellidos")}
              required
              disabled={clienteExistente || procesoCreado}
              readOnly={clienteExistente || procesoCreado}
            />
          </Grid>
        </Grid>
      </SuiBox>

      <SuiBox className={classes.formSection}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Tipo Persona
            </SuiTypography>
            <Select
              value={formData.clienteTipoPersona}
              onChange={handleChange("clienteTipoPersona")}
              displayEmpty
              fullWidth
              disabled={clienteExistente || procesoCreado}
              className={classes.select}
              sx={clienteExistente || procesoCreado ? { opacity: 0.8 } : {}}
            >
              <MenuItem value="Persona Natural No Comerciante">
                Persona Natural No Comerciante
              </MenuItem>
              <MenuItem value="Persona Natural Pequeño(a) Comerciante">
                Persona Natural Pequeño(a) Comerciante
              </MenuItem>
            </Select>
          </Grid>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Correo Electrónico
            </SuiTypography>
            <SuiInput
              value={formData.clienteEmail}
              onChange={handleChange("clienteEmail")}
              type="email"
              disabled={clienteExistente || procesoCreado}
              readOnly={clienteExistente || procesoCreado}
            />
          </Grid>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Celular
            </SuiTypography>
            <SuiInput
              value={formData.clienteCelular}
              onChange={handleChange("clienteCelular")}
              disabled={clienteExistente || procesoCreado}
              readOnly={clienteExistente || procesoCreado}
            />
          </Grid>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Dirección
            </SuiTypography>
            <SuiInput
              value={formData.clienteDireccion}
              onChange={handleChange("clienteDireccion")}
              disabled={clienteExistente || procesoCreado}
              readOnly={clienteExistente || procesoCreado}
            />
          </Grid>
        </Grid>
      </SuiBox>

      <SuiBox className={classes.formSection}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Género
            </SuiTypography>
            <Select
              value={formData.clienteGenero}
              onChange={handleChange("clienteGenero")}
              displayEmpty
              fullWidth
              disabled={clienteExistente || procesoCreado}
              className={classes.select}
              sx={clienteExistente || procesoCreado ? { opacity: 0.8 } : {}}
            >
              <MenuItem value="FE">FEMENINO</MenuItem>
              <MenuItem value="MA">MASCULINO</MenuItem>
              <MenuItem value="OTRO">OTRO</MenuItem>
              <MenuItem value="NR">NO RESPONDE</MenuItem>
            </Select>
          </Grid>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Tipo Trabajador
            </SuiTypography>
            <Select
              value={formData.clienteTipoTrabajador}
              onChange={handleChange("clienteTipoTrabajador")}
              displayEmpty
              fullWidth
              disabled={clienteExistente || procesoCreado}
              className={classes.select}
              sx={clienteExistente || procesoCreado ? { opacity: 0.8 } : {}}
            >
              <MenuItem value="INDEPENDIENTE">INDEPENDIENTE</MenuItem>
              <MenuItem value="EMPLEADO(A)">EMPLEADO(A)</MenuItem>
              <MenuItem value="PENSIONADO">PENSIONADO</MenuItem>
            </Select>
          </Grid>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Sector
            </SuiTypography>
            <Select
              value={formData.clienteSector}
              onChange={handleChange("clienteSector")}
              displayEmpty
              fullWidth
              disabled={clienteExistente || procesoCreado}
              className={classes.select}
              sx={clienteExistente || procesoCreado ? { opacity: 0.8 } : {}}
            >
              <MenuItem value="PUBLICO">PUBLICO</MenuItem>
              <MenuItem value="PRIVADO">PRIVADO</MenuItem>
              <MenuItem value="MIXTO">MIXTO</MenuItem>
              <MenuItem value="OTRO">OTRO</MenuItem>
            </Select>
          </Grid>
          <Grid size={3}>
            <SuiTypography variant="h6" fontWeight="light">
              Fecha Nacimiento
            </SuiTypography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={
                  formData.clienteFechaNacimiento ? dayjs(formData.clienteFechaNacimiento) : null
                }
                onChange={handleDateChange}
                format="DD/MM/YYYY"
                disabled={clienteExistente || procesoCreado}
                renderInput={(params) => (
                  <SuiInput
                    {...params}
                    icon={{
                      component: <CalendarTodayIcon />,
                      direction: "right",
                    }}
                    className={classes.datePicker}
                    sx={{
                      ...(clienteExistente || procesoCreado ? { opacity: 0.8 } : {}),
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        transform: "translate(0, -10px) scale(1)",
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </SuiBox>

      <SuiBox className={classes.formSection}>
        <SuiTypography variant="h6">Datos del Proceso</SuiTypography>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={4}>
            <SuiTypography variant="h6" fontWeight="light">
              Tipo Proceso
            </SuiTypography>
            <Select
              value={formData.tipoSeleccionado}
              onChange={handleChange("tipoSeleccionado")}
              displayEmpty
              fullWidth
              disabled={procesoCreado}
              className={classes.select}
              sx={procesoCreado ? { opacity: 0.8 } : {}}
            >
              {formData.tiposProceso &&
                formData.tiposProceso.map((tipo) => (
                  <MenuItem key={tipo.id} value={tipo.id}>
                    {tipo.nombre}
                  </MenuItem>
                ))}
            </Select>
          </Grid>
          <Grid size={4}>
            <SuiTypography variant="h6" fontWeight="light">
              Ciudad
            </SuiTypography>
            <Select
              value={formData.sedeSeleccionada}
              onChange={handleChange("sedeSeleccionada")}
              displayEmpty
              fullWidth
              disabled={procesoCreado}
              className={classes.select}
              sx={procesoCreado ? { opacity: 0.8 } : {}}
            >
              {formData.sedes &&
                formData.sedes.map((sede) => (
                  <MenuItem key={sede.id} value={sede.id}>
                    {sede.ciudad}
                  </MenuItem>
                ))}
            </Select>
          </Grid>
          <Grid size={4}>
            <SuiTypography variant="h6" fontWeight="light">
              Finalidad
            </SuiTypography>
            <Select
              value={formData.finalidad}
              onChange={handleChange("finalidad")}
              displayEmpty
              fullWidth
              disabled={procesoCreado}
              className={classes.select}
              sx={procesoCreado ? { opacity: 0.8 } : {}}
            >
              <MenuItem value="LIQUIDACION">Liquidación</MenuItem>
              <MenuItem value="ACUERDO">Acuerdo</MenuItem>
              <MenuItem value="OTRO">Otro</MenuItem>
            </Select>
          </Grid>
        </Grid>
      </SuiBox>

      <SuiBox className={classes.formSection}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={6}>
            <InputLabel className={classes.inputLabel}>
              <Checkbox
                checked={formData.poseeBienes || false}
                onChange={handleCheckboxChange("poseeBienes")}
                color="primary"
              />
              ¿Posee bienes muebles o inmuebles?
            </InputLabel>
          </Grid>
          <Grid size={6}>
            <InputLabel className={classes.inputLabel}>
              <Checkbox
                checked={formData.procesosJudiciales || false}
                onChange={handleCheckboxChange("procesosJudiciales")}
                color="primary"
              />
              ¿Tiene procesos judiciales?
            </InputLabel>
          </Grid>
          <Grid size={6}>
            <InputLabel className={classes.inputLabel}>
              <Checkbox
                checked={formData.personasACargo || false}
                onChange={handleCheckboxChange("personasACargo")}
                color="primary"
              />
              ¿Tiene personas a cargo?
            </InputLabel>
          </Grid>
          <Grid size={6}>
            <InputLabel className={classes.inputLabel}>
              <Checkbox
                checked={formData.sociedadesProceso || false}
                onChange={handleCheckboxChange("sociedadesProceso")}
                color="primary"
              />
              ¿Tiene sociedades (Conyugal o Patrimonial)?
            </InputLabel>
          </Grid>
        </Grid>
      </SuiBox>

      <SuiBox className={classes.formSection}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={12}>
            <InputLabel className={classes.inputLabel}>Causas de la Solicitud</InputLabel>
            <SuiInput
              value={formData.causas}
              placeholder="Causas de la Solicitud"
              multiline
              rows={5}
              onChange={handleChange("causas")}
              className={classes.causasTextarea}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  padding: "12px",
                  backgroundColor: "#f8f9fa",
                },
              }}
            />
          </Grid>
        </Grid>
      </SuiBox>
    </SuiBox>
  );
}

ClienteForm.propTypes = {
  formData: PropTypes.shape({
    id: PropTypes.number,
    clienteId: PropTypes.number,
    clienteIdentificacion: PropTypes.string.isRequired,
    clienteTipoIdentificacion: PropTypes.string.isRequired,
    clienteNombres: PropTypes.string.isRequired,
    clienteApellidos: PropTypes.string.isRequired,
    clienteGenero: PropTypes.string,
    clienteFechaNacimiento: PropTypes.string,
    clienteDireccion: PropTypes.string,
    clienteCelular: PropTypes.string,
    clienteEmail: PropTypes.string,
    clienteTipoTrabajador: PropTypes.string,
    clienteTipoPersona: PropTypes.string,
    clienteSector: PropTypes.string,
    tipoSeleccionado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tiposProceso: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        nombre: PropTypes.string,
      })
    ),
    sedeSeleccionada: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sedes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        ciudad: PropTypes.string,
      })
    ),
    finalidad: PropTypes.string,
    descripcion: PropTypes.string,
    causas: PropTypes.string,
    poseeBienes: PropTypes.bool,
    procesosJudiciales: PropTypes.bool,
    personasACargo: PropTypes.bool,
    sociedadesProceso: PropTypes.bool,
    nombre: PropTypes.string,
    resp_asistente: PropTypes.number,
    compromisoMensual: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  procesoCreado: PropTypes.bool,
  procesoId: PropTypes.number,
};

export default ClienteForm;
